import type {
  DealSow,
  SowReadinessReport,
  SowRenderContext,
  SowVersionSummary,
} from '@protoss/shared';
import { slugifyHeading, sowEditorContentKey } from '@protoss/shared';
import type { DocumentChild, DocumentContract } from '@protoss/doc';
import {
  assembleNarrativeMarkdown,
  conditionalSectionHeadings,
  findDataTableRenderChildren,
  findSignatureBlockChildren,
  filterInactiveWhenBranches,
  FILLABLE_PREFIX,
  fillablePlaceholder,
  injectSowAiSections,
  injectSowDataTables,
  injectSowSignatureBlock,
  isDataDrivenSowToken,
  parseLogicalSections,
  sectionHeadings,
  SIGNATURE_DATA_KEY,
} from '@protoss/doc';
import { FileTextIcon, RichTextEditor, Select, SelectItem, SkeletonBlock } from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { DocContractView } from '../../../components/doc';
import { resolveTokenText } from '../../../components/doc/previewSample';
import { formatDateTimeWithYear } from '../../../lib/datetime';
import { parseContentToTiptap } from '../../../lib/tiptap';
import { useBrandPrimaryColor } from '../../../hooks/useBrandPrimaryColor';
import { salesApi } from '../services/salesApi';
import { DealSowDocView } from './DealSowDocView';
import { formatDealVersionLabel } from './versioning';

// Temporarily hide the version-history dropdown in the SOW UI. Flip to `true`
// to restore it (the version plumbing below stays wired).
const SHOW_VERSION_DROPDOWN = false;

export interface DealSowPaneProps {
  sow: DealSow | null;
  loading: boolean;
  error: string | null;
  readinessReport?: SowReadinessReport | null;
  isEditing: boolean;
  /** Autosave hint shown while editing (Saving… → Saved). */
  saveStatus?: 'idle' | 'saving' | 'saved';
  draftContent: Record<string, unknown> | null;
  /** Update the in-session editor draft; `null` clears it (fall back to the assembled doc). */
  onDraftChange: (json: Record<string, unknown> | null) => void;
  onRetry?: () => void;
  dealUuid?: string;
  /**
   * Strict category gating: `false` when the deal's category has no matching SOW
   * profile, so the pane shows a "not configured" state and generation/export is
   * disabled. `undefined`/`true` = available (default).
   */
  available?: boolean;
  /** Read-only version history for the dropdown (newest first); current is flagged. */
  versions?: SowVersionSummary[];
  /** Currently-selected version uuid (null/undefined = current/working version). */
  selectedVersionUuid?: string | null;
  /** Select a version to view read-only (and export). */
  onSelectVersion?: (uuid: string) => void;
  /** True when a non-current version is being viewed (drives the read-only banner). */
  viewingPastVersion?: boolean;
}

function renderEmptyState(props: { title: string; body: string; tone: 'neutral' | 'danger' }) {
  return (
    <div className={`deal-sow-pane__empty deal-sow-pane__empty--${props.tone}`}>
      <div className="deal-sow-pane__empty-icon" aria-hidden="true">
        <FileTextIcon width={18} height={18} />
      </div>
      <div className="deal-sow-pane__empty-copy">
        <h3 className="deal-sow-pane__empty-title">{props.title}</h3>
        <p className="deal-sow-pane__empty-body">{props.body}</p>
      </div>
    </div>
  );
}

export function DealSowPane({
  sow,
  loading,
  error,
  readinessReport,
  isEditing,
  saveStatus = 'idle',
  draftContent,
  onDraftChange,
  onRetry,
  dealUuid,
  available,
  versions,
  selectedVersionUuid,
  onSelectVersion,
  viewingPastVersion,
}: DealSowPaneProps) {
  // Workspace brand color, so the editor's table headers/borders match the
  // branded export/view (null while loading -> default neutral styling).
  const brandColor = useBrandPrimaryColor();
  // True while the read-only view re-fetches the resolved document in the
  // background (cached content stays on screen) - drives the "Refreshing…" hint.
  const [docRefreshing, setDocRefreshing] = React.useState(false);

  // In EDIT mode, fetch the resolved render context (contract + sample) so the data
  // tables (dynamicBlocks) can be shown read-only inline alongside the editable
  // narrative. Degrades silently to prose-only on prereq-missing (422) / error.
  const [editRenderContext, setEditRenderContext] = React.useState<SowRenderContext | null>(null);
  // True while the edit render context is being fetched. Used to hold the editor's mount until the
  // fresh contract assembly is ready (so it never mounts with the stale saved tiptapContent).
  const [editContextLoading, setEditContextLoading] = React.useState(false);
  const loadEditRenderContext = React.useCallback(async () => {
    if (!(isEditing && dealUuid && !viewingPastVersion)) {
      setEditRenderContext(null);
      setEditContextLoading(false);
      return;
    }
    setEditContextLoading(true);
    try {
      setEditRenderContext(await salesApi.getSowRenderContext(dealUuid));
    } catch {
      setEditRenderContext(null);
    } finally {
      setEditContextLoading(false);
    }
  }, [isEditing, dealUuid, viewingPastVersion]);
  React.useEffect(() => {
    void loadEditRenderContext();
  }, [loadEditRenderContext]);

  // Delete (excluded:true) / restore (false) a conditional section, from the inline control on
  // its heading. Only the exclusion FLAG is persisted - the section's content stays in sow.content
  // (and in the editor, struck through), so a deleted section can be restored unchanged. Re-fetch
  // the render context so the fresh `excludedSections` flips the strikethrough band; the in-session
  // draft (prose edits) is preserved - the editor only re-decorates, it does not re-assemble.
  const handleToggleExcludedSection = React.useCallback(
    async (slug: string, excluded: boolean) => {
      if (!sow?.uuid || !dealUuid) return;
      await salesApi.setSowExcludedSection(dealUuid, sow.uuid, slug, excluded);
      await loadEditRenderContext();
    },
    [sow?.uuid, dealUuid, loadEditRenderContext]
  );

  // Save / reset a data-table cell override. In the editor the override STATUS lives on the section
  // heading (spinner while saving/resetting, then the "Updated" badge), so we track per-dataKey
  // saving/error state here (the heading annotation reads it via getHeadingOverride). Re-fetch so
  // the resolved data refreshes; the doc content is unchanged, so only the tables/heading re-render.
  const [dataSaving, setDataSaving] = React.useState<Set<string>>(() => new Set());
  const [dataError, setDataError] = React.useState<Set<string>>(() => new Set());
  const addKey = (s: Set<string>, k: string) => new Set(s).add(k);
  const dropKey = (s: Set<string>, k: string) => {
    const n = new Set(s);
    n.delete(k);
    return n;
  };
  const handleSaveDataOverride = React.useCallback(
    async (dataKey: string, rows: Array<Record<string, string>>) => {
      if (!sow?.uuid || !dealUuid) return;
      setDataSaving((s) => addKey(s, dataKey));
      setDataError((s) => dropKey(s, dataKey));
      try {
        await salesApi.saveSowDataOverride(dealUuid, sow.uuid, dataKey, rows);
        await loadEditRenderContext();
      } catch {
        setDataError((s) => addKey(s, dataKey));
      } finally {
        setDataSaving((s) => dropKey(s, dataKey));
      }
    },
    [sow?.uuid, dealUuid, loadEditRenderContext]
  );
  const handleResetDataOverride = React.useCallback(
    async (dataKey: string) => {
      if (!sow?.uuid || !dealUuid) return;
      setDataSaving((s) => addKey(s, dataKey));
      setDataError((s) => dropKey(s, dataKey));
      try {
        await salesApi.resetSowDataOverride(dealUuid, sow.uuid, dataKey);
        await loadEditRenderContext();
      } catch {
        setDataError((s) => addKey(s, dataKey));
      } finally {
        setDataSaving((s) => dropKey(s, dataKey));
      }
    },
    [sow?.uuid, dealUuid, loadEditRenderContext]
  );

  // User-excluded conditional sections stay visible and struck through so they can be restored.
  // Mutually-exclusive `when` branches are different: resolve those before assembly so Edit,
  // View, and export expose exactly the same pricing model and a no-op save cannot persist both.
  const rawEditContract = editRenderContext?.contract as DocumentContract | undefined;
  const editExcluded = editRenderContext?.excludedSections;
  const editSample = editRenderContext?.sample;
  const editContract = React.useMemo(
    () =>
      rawEditContract && editSample
        ? filterInactiveWhenBranches(rawEditContract, (path) => resolveTokenText(path, editSample))
        : rawEditContract,
    [rawEditContract, editSample]
  );
  // Every resolved data table is cell-editable in the editor (override-wins, like the View); the
  // signature block stays read-only (it isn't a `data` array). Override/drift drive the badges.
  const editableDataKeys = React.useMemo(
    () => new Set(Object.keys(editSample?.data ?? {})),
    [editSample?.data]
  );
  const editDataOverridden = React.useMemo(
    () => new Set(editRenderContext?.dataOverrides ?? []),
    [editRenderContext?.dataOverrides]
  );
  const editDataDrift = React.useMemo(
    () => new Set(editRenderContext?.dataDrift ?? []),
    [editRenderContext?.dataDrift]
  );
  // Map each section heading slug to the data-table dataKey(s) it anchors (assembleNarrativeMarkdown
  // emits a heading for every section that has a table, so the table's section heading is always in
  // the editor). Drives the heading "Updated" override annotation.
  const dataKeysByHeadingSlug = React.useMemo(() => {
    const map = new Map<string, string[]>();
    if (!editContract) return map;
    for (const s of parseLogicalSections(editContract)) {
      if (!s.headingText) continue;
      const keys = (s.body as Array<{ type?: string; dataKey?: string }>)
        .filter((b) => b.type === 'dynamicBlock' && typeof b.dataKey === 'string')
        .map((b) => b.dataKey as string);
      if (keys.length > 0) map.set(slugifyHeading(s.headingText), keys);
    }
    return map;
  }, [editContract]);
  // Per-heading data-override status for the heading annotation: non-null when one of the section's
  // tables is overridden / saving / errored. A new function identity on each state change re-decorates.
  const getHeadingOverride = React.useCallback(
    (headingText: string) => {
      const keys = dataKeysByHeadingSlug.get(slugifyHeading(headingText));
      if (!keys) return null;
      for (const dataKey of keys) {
        const overridden = editDataOverridden.has(dataKey);
        const saving = dataSaving.has(dataKey);
        const error = dataError.has(dataKey);
        if (overridden || saving || error) {
          return { dataKey, overridden, drift: editDataDrift.has(dataKey), saving, error };
        }
      }
      return null;
    },
    [dataKeysByHeadingSlug, editDataOverridden, editDataDrift, dataSaving, dataError]
  );
  // Original generated blocks for an AI section slug (from the resolved narrative), so the editor
  // can revert an edited AI section from its heading "Updated" badge.
  const getAiOriginalContent = React.useCallback(
    (slug: string): Record<string, unknown>[] | null => {
      const md = editSample?.narrative?.[`${slug}_ai`];
      if (typeof md !== 'string' || !md.trim()) return null;
      const doc = parseContentToTiptap(md);
      return (doc?.content as Record<string, unknown>[] | undefined) ?? null;
    },
    [editSample]
  );
  // Token paths carrying a manual override (full paths, e.g. "context.clientName"). Their values
  // are already overlaid into sample.tokens; this drives the "Updated" badge on those tokens
  // (inline in prose AND in the signature table's client/company cells).
  const editTokenOverridden = React.useMemo(
    () => new Set(editRenderContext?.tokenOverridden ?? []),
    [editRenderContext?.tokenOverridden]
  );
  // Renderer for an injected sowDataTable node: render the resolved dynamicBlock via
  // DocContractView. Data tables are inline cell-editable (saved as overrides, still highlighted +
  // "Updated" badge); the signature block has no editableDataKey so it stays read-only.
  const renderDataTable = React.useCallback(
    (attrs: { dataKey: string; includeHeading?: boolean }): React.ReactNode => {
      if (!editContract || !editSample) return null;
      // The signature/acceptance block is static contract chrome (data-driven legal names),
      // injected read-only so the editor shows it with the values highlighted.
      const isSignature = attrs.dataKey === SIGNATURE_DATA_KEY;
      const children = isSignature
        ? findSignatureBlockChildren(editContract)
        : findDataTableRenderChildren(editContract, attrs.dataKey, !!attrs.includeHeading);
      if (children.length === 0) return null;
      return (
        <DocContractView
          content={children as DocumentChild[]}
          mode="sample"
          sample={editSample}
          brandColor={brandColor ?? undefined}
          highlightGenerated
          editableDataKeys={isSignature ? undefined : editableDataKeys}
          overriddenKeys={editDataOverridden}
          driftKeys={editDataDrift}
          // So the signature table's client/company name tokens show the "Updated" badge + the
          // overridden value when overridden elsewhere (token overrides sync across the SOW).
          tokenOverridden={editTokenOverridden}
          // Tint the signature/acceptance table like the data tables (its content is just that
          // table, so this only tints the signature table).
          tintTables={isSignature}
          onSaveOverride={sow?.uuid ? handleSaveDataOverride : undefined}
          onResetOverride={sow?.uuid ? handleResetDataOverride : undefined}
          // In the editor the override status (Updated badge + hint + spinner) lives on the
          // section heading (the GeneratedSectionBands annotation), so hide the in-table bar.
          hideEditBar
        />
      );
    },
    [
      editContract,
      editSample,
      brandColor,
      editableDataKeys,
      editDataOverridden,
      editDataDrift,
      editTokenOverridden,
      sow?.uuid,
      handleSaveDataOverride,
      handleResetDataOverride,
    ]
  );
  // Resolve inline `{{protoss:...}}` binding tokens in the editor to their values so
  // computed content (dates, fees, names) shows like the document instead of raw token
  // text. Highlights data-driven values (context/calc), and flags overridden ones as `updated`.
  // While the render context is still loading, returns a `loading` marker so the chip shows a
  // spinner (not its label); returns null for genuinely-unresolved tokens (label chip authoring).
  const resolveToken = React.useCallback(
    (
      path: string
    ): { value: string; highlight?: boolean; updated?: boolean } | { loading: true } | null => {
      if (!editSample) return { loading: true };
      // Unbound fillable placeholder: always resolves to a non-null value (the filled value or a
      // visible placeholder) so the chip is double-click-fillable even when empty - unlike a plain
      // unresolved token, which falls back to the non-editable authoring chip.
      if (path.startsWith(FILLABLE_PREFIX)) {
        const filled = editSample.tokens[path];
        const value =
          typeof filled === 'string' && filled.trim()
            ? filled
            : fillablePlaceholder(path.slice(FILLABLE_PREFIX.length));
        return { value, highlight: true, updated: editTokenOverridden.has(path) };
      }
      const raw = editSample.tokens[path] ?? editSample.tokens[path.split(/[.:]/).pop() ?? path];
      if (raw == null || raw === '') return null;
      return {
        value: String(raw),
        highlight: isDataDrivenSowToken(path),
        updated: editTokenOverridden.has(path),
      };
    },
    [editSample, editTokenOverridden]
  );
  // Set (string) or clear (null) a manual token override; persist + re-fetch so the editor
  // re-resolves the token everywhere to the new value (+ Updated badge). The doc content is
  // unchanged (the token nodes stay), so the editor only re-resolves - no reset/cursor jump.
  const handleTokenOverride = React.useCallback(
    async (path: string, value: string | null) => {
      if (!sow?.uuid || !dealUuid) return;
      await salesApi.setSowTokenOverride(dealUuid, sow.uuid, path, value);
      await loadEditRenderContext();
    },
    [sow?.uuid, dealUuid, loadEditRenderContext]
  );
  // Section bands to mirror the view's block-level highlights in the editor. Conditional
  // sections' prose is in the editable narrative, so band them by heading (the band also carries
  // the inline Delete/Restore control). `deleted` = conditional sections the user has removed -
  // they stay in the doc but render struck-through. AI prose is NOT banded by heading here: it
  // lives in its own editable `sowAiSection` node, which carries the "AI-generated" band
  // intrinsically (so the band can't be lost on edit).
  const sectionBands = React.useMemo(() => {
    const conditional = editContract ? conditionalSectionHeadings(editContract) : [];
    const excluded = new Set(editExcluded ?? []);
    return {
      conditional,
      ai: [] as string[],
      deleted: conditional.filter((h) => excluded.has(slugifyHeading(h))),
    };
  }, [editContract, editExcluded]);
  // Lock the contract's section headings (read-only): they are the narrative split keys, so
  // editing one re-slugs and orphans its body (and the View renders the heading from the
  // template, not the editor - so a rename has no effect anyway). Edit prose, not structure.
  const lockedHeadings = React.useMemo(
    () => (editContract ? sectionHeadings(editContract) : []),
    [editContract]
  );
  // The COMPLETE editable narrative assembled from the full contract structure (every section
  // heading + fixed prose), then AI sections injected as editable `sowAiSection` nodes in
  // their own sections - so the editor edits the whole document, AI prose keeps its
  // provenance, and read-only data tables anchor in their own sections instead of clumping at
  // the end. Null until the render context loads; the saved draft (in-session edits) always
  // takes precedence over it - we inject AI only on the FRESH assembly, never per-render, so
  // edited AI text in the draft is never clobbered.
  const assembledContent = React.useMemo(() => {
    if (!editContract || !editSample) return null;
    const base = parseContentToTiptap(
      assembleNarrativeMarkdown(editContract, editSample.narrative)
    );
    const withAi = injectSowAiSections(base, editContract, editSample.narrative);
    // Inject the read-only data-table nodes ONCE here (same as the AI nodes), NOT per render.
    // Re-injecting on every keystroke (against `draftContent`) re-runs strip+anchor on the live
    // doc, which (a) isn't a stable fixed point so the editor's `content` prop changes every
    // edit -> the content-sync `setContent` fires -> the CURSOR jumps to the doc end, and (b)
    // can re-append a table (e.g. the rate card) to the bottom instead of its section.
    const withTables = injectSowDataTables(withAi, editContract);
    // The Acceptance signature table is static contract chrome (not narrative) - inject it
    // read-only at the doc end so the editor shows it (data-driven legal names highlighted).
    return injectSowSignatureBlock(withTables, editContract);
  }, [editContract, editSample]);

  if (available === false) {
    return renderEmptyState({
      title: 'SOW not configured for this category',
      body: "This deal's category has no SOW configuration. Pick a configured category, or set one up for this category in Products.",
      tone: 'neutral',
    });
  }

  if (loading && !sow) {
    return (
      <div
        className="deal-sow-pane deal-sow-pane--loading"
        aria-busy="true"
        aria-label="Loading SOW"
      >
        {[0, 1, 2, 3].map((item) => (
          <SkeletonBlock key={item} className="deal-sow-pane__skeleton" />
        ))}
      </div>
    );
  }

  if (error && !sow) {
    return (
      <div className="deal-sow-pane__empty deal-sow-pane__empty--danger">
        <div className="deal-sow-pane__empty-icon" aria-hidden="true">
          <FileTextIcon width={18} height={18} />
        </div>
        <div className="deal-sow-pane__empty-copy">
          <h3 className="deal-sow-pane__empty-title">SOW unavailable</h3>
          {onRetry ? (
            <PageErrorState message={error} onRetry={onRetry} />
          ) : (
            <p className="deal-sow-pane__empty-body">{error}</p>
          )}
          {readinessReport && readinessReport.blockers.length > 0 && (
            <ul className="deal-sow-pane__readiness-blockers">
              {readinessReport.blockers.map((f) => (
                <li key={f.fieldKey} className="deal-sow-pane__readiness-blocker">
                  {f.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  if (!sow) {
    return renderEmptyState({
      title: 'No SOW yet',
      body: '',
      tone: 'neutral',
    });
  }

  // Hold the editor's mount in edit mode until the fresh contract assembly is ready. Otherwise the
  // editor would mount with the stale saved `sow.tiptapContent` (the fallback below) while the edit
  // render context is still loading, and tiptap won't reactively swap its content once
  // `assembledContent` arrives - so contract changes (e.g. the fixed travel boilerplate + the
  // injected signature table) wouldn't appear. Only while LOADING (not on fetch error, which keeps
  // the prose-only fallback) and only when there is no in-session draft to preserve.
  if (isEditing && !draftContent && !assembledContent && editContextLoading) {
    return (
      <div className="deal-sow-pane deal-sow-pane--loading" aria-busy="true" aria-label="Loading SOW editor">
        {[0, 1, 2, 3].map((item) => (
          <SkeletonBlock key={item} className="deal-sow-pane__skeleton" />
        ))}
      </div>
    );
  }

  // In edit mode the data tables + AI nodes are already injected into `assembledContent`
  // (the fresh assembly) and carried in `draftContent` (in-session edits), so use the chosen
  // doc AS-IS - never re-inject per render (that caused the cursor-jump + rate-card-at-bottom
  // bugs). `draftContent` (the editor's own JSON) already contains the read-only nodes.
  const editorContent = isEditing
    ? (draftContent ?? assembledContent ?? sow.tiptapContent)
    : (sow.tiptapContent ?? parseContentToTiptap(sow.sowContent));

  return (
    <div className="deal-sow-pane">
      <div className="deal-sow-pane__meta">
        <div className="deal-sow-pane__meta-items">
          {SHOW_VERSION_DROPDOWN && versions && versions.length > 1 && onSelectVersion ? (
            <span className="deal-version-select">
              <Select
                value={selectedVersionUuid ?? versions[0]?.uuid ?? ''}
                onValueChange={onSelectVersion}
                size="sm"
              >
                {versions.map((v) => (
                  <SelectItem key={v.uuid} value={v.uuid}>
                    {formatDealVersionLabel({
                      version: v.version,
                      timestamp: v.updatedAt,
                      edited: v.userEdited,
                      isCurrent: v.isCurrent,
                    })}
                  </SelectItem>
                ))}
              </Select>
            </span>
          ) : null}
          <span className="deal-sow-pane__meta-item">
            Updated {formatDateTimeWithYear(sow.updatedAt)}
          </span>
          {viewingPastVersion ? (
            <span className="deal-sow-pane__meta-item deal-sow-pane__meta-item--warning">
              Viewing a past version (read-only)
            </span>
          ) : null}
        </div>
        {isEditing && saveStatus !== 'idle' ? (
          <span className="deal-sow-pane__autosave-status">
            {saveStatus === 'saving' ? 'Saving…' : 'Saved'}
          </span>
        ) : null}
        {!isEditing && docRefreshing ? (
          <span className="deal-sow-pane__autosave-status">Refreshing…</span>
        ) : null}
      </div>

      <div className="deal-sow-pane__editor-shell">
        {isEditing ? (
          // Edit route: full Tiptap editor over the whole SOW. Conditional sections carry an
          // inline Delete/Restore control in their heading band (deleted ones render struck-through).
          <RichTextEditor
            content={editorContent}
            readOnly={false}
            toolbar={true}
            className="deal-sow-pane__editor"
            brandColor={brandColor ?? undefined}
            renderDataTable={editContract ? renderDataTable : undefined}
            resolveToken={resolveToken}
            onTokenOverride={sow?.uuid ? handleTokenOverride : undefined}
            sectionBands={sectionBands}
            onToggleSectionDeleted={sow?.uuid ? handleToggleExcludedSection : undefined}
            headingOverride={getHeadingOverride}
            onResetDataOverride={sow?.uuid ? handleResetDataOverride : undefined}
            getAiOriginalContent={getAiOriginalContent}
            lockedHeadings={lockedHeadings}
            onUpdate={(json) => {
              // Until the first real edit, ignore updates that don't change what a save would
              // persist. On open the editor normalises the doc by itself (Tiptap appends an empty
              // paragraph after the signature table at the end), which used to arm the autosave
              // and mark an untouched SOW as user-edited (409 SOW_HAS_USER_EDITS on Regenerate).
              if (
                !draftContent &&
                sowEditorContentKey(json) === sowEditorContentKey(editorContent)
              ) {
                return;
              }
              onDraftChange(json);
            }}
          />
        ) : dealUuid && !viewingPastVersion ? (
          // View route, current version: resolved read-only document.
          <DealSowDocView
            dealUuid={dealUuid}
            sowUuid={sow.uuid}
            onRefreshingChange={setDocRefreshing}
          />
        ) : (
          // Past version (read-only) or no dealUuid: flat read-only render of this
          // version's own content (the render-context endpoint only resolves the
          // current SOW, so past versions show their stored content instead).
          <RichTextEditor
            content={editorContent}
            readOnly={true}
            toolbar={false}
            className="deal-sow-pane__editor"
            brandColor={brandColor ?? undefined}
            onUpdate={(json) => onDraftChange(json)}
          />
        )}
      </div>
    </div>
  );
}
